import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SERVICES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", company: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your inquiry has been received.");
      setForm(initial);
      setDone(true);
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 md:py-36 border-t border-white/10" data-testid="contact">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <span className="text-primary text-sm font-semibold tracking-[0.25em] uppercase">Contact</span>
          <h2 className="mt-4 font-heading font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-foreground">
            Let&rsquo;s build<br />quality in.
          </h2>
          <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-md">
            Tell us about your project. Whether it&rsquo;s QA/QC setup, material inspection or standards
            guidance, we&rsquo;ll get back to you with a clear next step.
          </p>
          <div className="mt-10 space-y-4">
            {["Independent, standards-driven inspection", "NABL-accredited lab coordination", "Documentation that is always audit-ready"].map((t) => (
              <div key={t} className="flex items-center gap-3 text-foreground/80">
                <CheckCircle2 size={18} className="text-primary shrink-0" /> {t}
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <form
            onSubmit={submit}
            className="rounded-2xl border border-white/10 bg-card p-8 md:p-10 space-y-6"
            data-testid="contact-form"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Full name *</Label>
                <Input id="name" value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="contact-email" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." data-testid="contact-phone" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input id="company" value={form.company} onChange={set("company")} placeholder="Company / firm" data-testid="contact-company" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Service of interest</Label>
              <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                <SelectTrigger data-testid="contact-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
                <SelectContent>
                  {SERVICES.map((s) => (
                    <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message *</Label>
              <Textarea id="message" value={form.message} onChange={set("message")} rows={5} placeholder="Tell us about your project and how we can help..." data-testid="contact-message" />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-8 py-3.5 text-sm font-semibold hover:bg-primary/90 hover:scale-[1.02] active:scale-95 transition-transform duration-200 disabled:opacity-60"
              data-testid="contact-submit"
            >
              {loading ? "Sending..." : done ? "Sent" : "Send inquiry"}
              {done ? <CheckCircle2 size={16} /> : <Send size={16} />}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
